import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["gates", "copilotLayoutType"],
  _excluded2 = ["adapter", "prepareApp"];
import { useCallback, useEffect, useMemo, useState } from 'react';
import { setCopilotLayoutType } from 'quartz/framework/copilot/service';
import { updateGridClasses } from '../internal/grid';
import { RouterProvider } from 'quartz-routing';
import Raven from 'raven-js';
import { getAuthResult } from 'quartz-auth/service';
import { Route } from 'quartz-routing/react-router';
import { GraniteRouter, useSoftNavigationContext } from './GraniteRoutes';
import { isGraniteEnabledForCurrentApp, getUngatedRemoteRoutes } from './granite-config';
import { getHostHistory } from 'quartz-routing/initialize';
import { activateApp } from './activateApp';
import { activateRemoteCss } from './remoteCss';
import { GlobalErrorBoundary } from '../components/GlobalErrorBoundary';
import { ErrorPage } from '../components/ErrorPage';
import { AppRoot } from '../internal/root-renderer';
import { isPromiseLike } from '../internal/isPromiseLike';
import { getCurrentPackageName, getCurrentPackageVersion } from 'quartz/framework/environment/service';
export { isGraniteEnabledForCurrentApp };
import { reconfigure } from 'raven-hubspot/configure';
import { primaryTrackers } from 'usage-tracker-container/primaryTrackerState';
import { getQuartzConfig } from 'quartz-config/evaluable';
import { getBasenameFromRouting } from '../util/getBasenameFromRouting';
import { createAttachHistory, createLocalNestedHistory } from './graniteHistory';
import { jsx as _jsx } from "react/jsx-runtime";
function LocalApp({
  history,
  activationResult,
  AppComponent,
  LayoutComponent,
  rhumbConfig,
  rhumbTrackVisibility,
  userId
}) {
  const packageName = getCurrentPackageName();
  const softNavigationContext = useSoftNavigationContext(packageName);
  const [result, setResult] = useState(null);
  useEffect(() => {
    function handleResult(r) {
      if (r && 'redirect' in r) {
        window.location.href = r.redirect;
        return;
      }
      setResult(r);
    }
    if (isPromiseLike(activationResult)) {
      activationResult.then(handleResult).catch(error => {
        Raven.captureException(error);
        setResult(null);
      });
    } else {
      handleResult(activationResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (result && 'onAfterRender' in result) {
      var _result$onAfterRender;
      (_result$onAfterRender = result.onAfterRender) === null || _result$onAfterRender === void 0 || _result$onAfterRender.call(result);
    }
  }, [result]);
  if (result === null) {
    return null;
  }
  const content = LayoutComponent ? /*#__PURE__*/_jsx(LayoutComponent, {
    children: /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props))
  }) : /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props));
  return /*#__PURE__*/_jsx(AppRoot, {
    history: history,
    rhumbConfig: rhumbConfig,
    rhumbTrackVisibility: rhumbTrackVisibility,
    userId: userId,
    softNavigationContext: softNavigationContext,
    isGraniteEnabled: true,
    children: content
  });
}
export function GraniteLocalApp(_ref) {
  let {
      gates,
      copilotLayoutType
    } = _ref,
    localAppProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    routeDefinitions,
    localRouteOverrides
  } = useMemo(() => getUngatedRemoteRoutes(gates), [gates]);
  const getRoutes = useCallback(() => ({
    routeDefinitions,
    localRouteOverrides
  }), [routeDefinitions, localRouteOverrides]);

  // Create the component type once per GraniteLocalApp mount — closes over
  // localAppProps so it can be used as a plain <Route component> with no
  // render prop. The inner useState lazy initializer ensures setup runs once
  // per route mount (not on every Switch re-render).
  const [LocalRouteComponent] = useState(() => {
    const {
        adapter,
        prepareApp
      } = localAppProps,
      rest = _objectWithoutPropertiesLoose(localAppProps, _excluded2);
    const packageName = getCurrentPackageName();
    const attachHistory = createAttachHistory(packageName);
    const packageVersion = getCurrentPackageVersion();
    const quartzConfig = getQuartzConfig();
    const historyBasename = /* quartzConfig.type === 'application' */true ? quartzConfig.historyBasename || getBasenameFromRouting(quartzConfig.UNSAFE_EXPERIMENTAL_routing) : null;
    if (!historyBasename) {
      throw new Error('historyBasename is required for granite local app');
    }
    function initLocalApp() {
      primaryTrackers.clear();
      reconfigure({
        project: packageName,
        release: packageVersion
      });
      if (copilotLayoutType !== undefined) {
        setCopilotLayoutType(copilotLayoutType);
        updateGridClasses(copilotLayoutType);
      }
      return Promise.all([activateApp(adapter, getAuthResult(), prepareApp), activateRemoteCss(packageName)]).then(([result]) => result);
    }
    function LocalRoute() {
      const [localHistory] = useState(() => createLocalNestedHistory(historyBasename));
      useEffect(() => {
        attachHistory(localHistory.history, localHistory.basename);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
      const [activationResult] = useState(initLocalApp);
      return /*#__PURE__*/_jsx(LocalApp, Object.assign({
        activationResult: activationResult,
        history: localHistory.history
      }, rest));
    }
    return LocalRoute;
  });
  return /*#__PURE__*/_jsx(GlobalErrorBoundary, {
    ErrorComponent: ErrorPage,
    children: /*#__PURE__*/_jsx(RouterProvider, {
      history: getHostHistory(),
      children: /*#__PURE__*/_jsx(GraniteRouter, {
        getRoutes: getRoutes,
        children: /*#__PURE__*/_jsx(Route, {
          path: "*",
          component: LocalRouteComponent
        })
      })
    })
  });
}